import { createRouter, createWebHistory } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useSupplierStore } from '@/store/supplier'
import { useMarketplaceStore } from '@/store/marketplace'
import { useNotificationStore } from '@/store/notifications'
import { useDynamicPopupStore } from '@/store/dynamicPopup'
import { useMessagesStore } from '@/store/messages'
import routes from './routes.js'
import { captureApiException } from '@/utility/sentry'

/* global __APP_BUILD_VERSION__ */

const VERSION_CHECK_THROTTLE_MS = 5000
const VERSION_RELOAD_GUARD_MS = 10000

async function shouldHardReloadOnNewVersion () {
    const lastCheck = Number(sessionStorage.getItem('last_version_check_at') || 0)
    const now = Date.now()

    if (now - lastCheck < VERSION_CHECK_THROTTLE_MS) {
        return false
    }

    sessionStorage.setItem('last_version_check_at', String(now))

    try {
        const response = await fetch(`/version.json?t=${now}`, {
            cache: 'no-store',
            headers: {
                'Cache-Control': 'no-cache',
                Pragma: 'no-cache'
            }
        })

        if (!response.ok) {
            return false
        }

        const payload = await response.json()
        const deployedVersion = String(payload?.version || '').trim()
        const currentVersion = String(typeof __APP_BUILD_VERSION__ !== 'undefined' ? __APP_BUILD_VERSION__ : '').trim()

        if (!deployedVersion || !currentVersion || deployedVersion === currentVersion) {
            return false
        }

        const lastReload = Number(sessionStorage.getItem('last_version_reload_at') || 0)
        if (now - lastReload < VERSION_RELOAD_GUARD_MS) {
            return false
        }

        sessionStorage.setItem('last_version_reload_at', String(now))
        return true
    } catch {
        return false
    }
}

const router = createRouter({
    history: createWebHistory(),
    linkActiveClass: 'active',
    scrollBehavior: () => ({
        top: 0
    }),
    routes
})

router.onError((error) => {
    const chunkFailedMessage = /Loading chunk [\d]+ failed|Loading CSS chunk [\d]+ failed|Failed to fetch dynamically imported module/i

    if (chunkFailedMessage.test(error.message)) {
        const lastReload = sessionStorage.getItem('last_chunk_reload')
        const now = Date.now()
        if (lastReload && (now - parseInt(lastReload)) <= 10000) {
            return
        }

        // Try to extract failing URL from the error message (if present)
        const urlMatch = String(error.message || '').match(/https?:\/\/[^'"\s)]+/)
        const resourceUrl = urlMatch ? urlMatch[0] : null

        async function checkResourceAvailable (url, attempts = 6, delayMs = 2000) {
            for (let i = 0; i < attempts; i++) {
                try {
                    const sep = url.includes('?') ? '&' : '?'
                    const resp = await fetch(url + sep + 't=' + Date.now(), { method: 'HEAD', cache: 'no-store' })
                    if (resp && resp.ok) {
                        const ct = (resp.headers && resp.headers.get ? resp.headers.get('content-type') : '') || ''
                        if (ct.includes('javascript') || ct.includes('application/javascript') || ct.includes('text/javascript') || resp.status === 200) {
                            return true
                        }
                    }
                } catch (err) {
                    // ignore and retry
                }
                await new Promise(r => setTimeout(r, delayMs))
            }
            return false
        }

        ;(async () => {
            try {
                if (resourceUrl) {
                    const ok = await checkResourceAvailable(resourceUrl, 6, 2000)
                    if (ok) {
                        sessionStorage.setItem('last_chunk_reload', now.toString())
                        window.location.reload()
                        return
                    }
                }

                // As a last resort, fall back to the old behavior but guard reload frequency
                sessionStorage.setItem('last_chunk_reload', now.toString())
                window.location.reload()
            } catch (e) {
                // If anything goes wrong, still attempt a guarded reload
                sessionStorage.setItem('last_chunk_reload', now.toString())
                window.location.reload()
            }
        })()
    }
})

router.beforeEach(async (to, from, next) => {

    // Handle legacy hash URLs - redirect to history mode
    if (window.location.hash && window.location.hash.startsWith('#/')) {
        const hashPath = window.location.hash.substring(1)
        const url = new URL(window.location.href)
        
        // Replace hash URL with history URL
        window.location.replace(url.origin + hashPath)
        return
    }

    try {
        const supplierStore = useSupplierStore()
        const messagesStore = useMessagesStore()
        const notificationStore = useNotificationStore()
        const marketplaceStore = useMarketplaceStore()

        const { resetSupplier } = supplierStore
        const { resetNotifications } = notificationStore
        const { resetMessages } = messagesStore


        // if route is towards projectlogin - reset every state
        if (to.name == 'ProjectLogin') {
            resetSupplier()
            resetMessages()
            resetNotifications()
        }

        // specific logic that contains two Components that has version and needs the data first
        if (to.name == 'PrivatMarkedsplads' || to.name == 'PrivatMarkedspladsNew' || to.name == 'Newsfeed') {
            if ((to.name == 'PrivatMarkedsplads' || to.name == 'PrivatMarkedspladsNew') && from.query.projectId) {
                marketplaceStore.setProjectId(from.query.projectId)
            }
        }

        const startDate = new Date(localStorage.getItem('start_date'))
        const currentDate = new Date()
        const excludedRoutes = ['kreditkort', 'ProjectLogin', 'ForgotPassword', 'Security', 'Unsubscribe', 'ResetPassword', 'AbonnementCaptcha']
        
        const token = localStorage.getItem('id_token_portalbitrix')
        if (!token && !excludedRoutes.includes(to.name)) {
            next('/login')
            return
        }

        if (to.path.startsWith('/webshop')) {
            let company = supplierStore.company

            if (!company || Object.keys(company).length === 0)
                company = await supplierStore.fetchCompany()

            const webshopAccess = company?.UF_CRM_COMPANY_PRISEN_WEBSHOP
            if (webshopAccess === 0 || webshopAccess == null) {
                next('/newsfeed')
                return
            }
        }

        if (startDate > currentDate && !excludedRoutes.includes(to.name)) {
            next('/rediger-kreditkort-oplysninger')
            return
        }
        next()
    } catch (error) {
        captureApiException(error, 'index.js', 'shouldHardReloadOnNewVersion')
        // Handle module loading errors during navigation (likely from new build)
        const lastReload = sessionStorage.getItem('last_nav_reload')
        const now = Date.now()
        
        if (!lastReload || (now - parseInt(lastReload)) > 10000) {
            sessionStorage.setItem('last_nav_reload', now.toString())
            window.location.reload()
        } else {
            // Prevent reload loop - just allow navigation to fail
            next(false)
        }
    }
})

router.afterEach(async (to, from) => {
    if (await shouldHardReloadOnNewVersion()) {
        window.location.reload()
        return
    }

    const excludedRoutes = ['kreditkort', 'ProjectLogin', 'ForgotPassword', 'Security', 'Unsubscribe', 'ResetPassword', 'AbonnementCaptcha']
    const token = localStorage.getItem('id_token_portalbitrix')
    
    if (token && !excludedRoutes.includes(to.name)) {
        const notificationStore = useNotificationStore()
        const messagesStore = useMessagesStore()
        const supplierStore = useSupplierStore()
        const dynamicPopupStore = useDynamicPopupStore()

        const { fetchNotifications } = notificationStore
        const { fetchMessages } = messagesStore
        const { getDynamicPopup } = dynamicPopupStore

        const { dynamicPopup } = storeToRefs(supplierStore)
        
        fetchNotifications(notificationStore.page)
        fetchMessages()
        getDynamicPopup(to.name)

        if (dynamicPopup.value) {
            if (to.name == 'fastcontract') {
                if (route.query.type && route.query.type != 'hvdk' && route.query.type.toLowerCase() != 'xl-byg') {
                    getDynamicPopup('fastContractAffiliate-hvdk')
                } else if (route.query.type && route.query.type.toLowerCase() == 'xl-byg') {
                    getDynamicPopup('fastContractAffiliate-hvdk-xlbyg')
                } else if (route.query.type && route.query.type == 'hvdk') {
                    getDynamicPopup('fastContract-hvdk')
                } else {
                    getDynamicPopup(to.name)
                }
            } else {
                getDynamicPopup(to.name)
            }
        }

    }
    if (import.meta.env.VITE_BITRIXAPI == 'https://bitrixapi.haandvaerker.dk/v1/') {
        window.dataLayer = window.dataLayer || []
        const fullPathWithHash = to.fullPath;
        const pagePath = to.path
        const pageLocation = window.location.href
        const affiliateType = to.query.type || ''
        dataLayer.push({
            'event': 'page_view',
            // 'event_category': 'SPA Navigation',
            // 'event_action': 'Route Changed',
            'event_label': fullPathWithHash,
    
            'page_location': pageLocation,
            'page_path': pagePath,
            'page_title': to.name || document.title,
            'affiliate_type': affiliateType
        })
    }
})

export default router